/**
 * Centralized Activity Logger Service
 * Provides consistent logging for all tab activities (Search Loads, Lane Pricing, Rate Insights, etc.)
 * Uses direct tai-khoan approach for reliable logging
 */

class ActivityLogger {
  constructor() {
    this.baseUrl = this.getApiBaseUrl();
  }

  // Get tai-khoan API base URL based on environment
  getApiBaseUrl() {
    const isLocal = window.location.hostname === 'localhost';
    return isLocal
      ? 'http://64.225.20.106:8081'  // Development - direct connection to tai-khoan container
      : '/tai-khoan';  // Production - nginx proxy to tai-khoan-container
  }

  /**
   * Log activity for any tab/function page
   * @param {Object} params - Activity logging parameters
   * @param {string} params.functionPage - Name of the function page (e.g., "Search Loads", "Lane Pricing", "Rate Insights")
   * @param {Object} params.searchParameters - The search/filter parameters used
   * @param {Object} params.auth - Auth0 authentication objects
   * @param {Function} params.auth.getAccessTokenSilently - Auth0 token function
   * @param {Object} params.auth.user - Auth0 user object
   * @param {Object} params.userData - User data from UserContext
   */
  async logActivity({ functionPage, searchParameters, auth, userData }) {
    try {
      const { getAccessTokenSilently, user } = auth;
      const accessToken = await getAccessTokenSilently();

      // Prepare the payload following the same format as Search Loads and Lane Pricing
      const payload = {
        user_id: userData?.id || user?.sub || 'unknown',
        user_sub: user?.sub || userData?.user_sub || 'unknown',
        function_page: functionPage,
        search_parameters: JSON.stringify(searchParameters)
      };

      console.log(`[ActivityLogger] Logging ${functionPage} activity with payload:`, payload);

      const response = await fetch(`${this.baseUrl}/add_search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`[ActivityLogger] ${functionPage} activity logged successfully:`, result);
        return { status: 'success', result };
      } else {
        console.warn(`[ActivityLogger] Failed to log ${functionPage} activity:`, response.status);
        return { status: 'error', error: `HTTP ${response.status}` };
      }
    } catch (logError) {
      console.warn(`[ActivityLogger] Failed to log ${functionPage} activity:`, logError);
      return { status: 'error', error: logError.message };
    }
  }

  /**
   * Convenience method for Search Loads
   */
  async logSearchLoads(searchParameters, auth, userData) {
    return this.logActivity({
      functionPage: 'Search Loads',
      searchParameters: {
        origin: searchParameters.origin,
        pickup_date: searchParameters.pickupDate,
        equipment: searchParameters.equipment,
        deadhead_miles: searchParameters.originRange
      },
      auth,
      userData
    });
  }

  /**
   * Convenience method for Lane Pricing
   */
  async logLanePricing(searchParameters, auth, userData) {
    return this.logActivity({
      functionPage: 'Lane Pricing',
      searchParameters: {
        origin: searchParameters.origin,
        destination: searchParameters.destination,
        equipment: searchParameters.equipment
      },
      auth,
      userData
    });
  }

  /**
   * Convenience method for Rate Insights
   */
  async logRateInsights(searchParameters, auth, userData) {
    return this.logActivity({
      functionPage: 'Rate Insights',
      searchParameters: {
        pickup_date_begin: searchParameters.pickup_date_begin,
        pickup_date_end: searchParameters.pickup_date_end,
        equipment_types: searchParameters.equipment_types,
        load_types: searchParameters.load_types,
        min_payment: searchParameters.min_payment,
        min_rate_per_mile: searchParameters.min_rate_per_mile
      },
      auth,
      userData
    });
  }

  /**
   * Convenience method for Broker Metrics
   */
  async logBrokerMetrics(searchParameters, auth, userData) {
    return this.logActivity({
      functionPage: 'Broker Metrics',
      searchParameters: {
        equipment_group: searchParameters.equipment_group,
        load_types: searchParameters.load_types,
        origin_states: searchParameters.origin_states,
        destination_states: searchParameters.destination_states,
        min_payment: searchParameters.min_payment,
        min_rpm: searchParameters.min_rpm
      },
      auth,
      userData
    });
  }

  /**
   * Convenience method for Route Optimization
   */
  async logRouteOptimization(searchParameters, auth, userData) {
    return this.logActivity({
      functionPage: 'Route Optimization',
      searchParameters: {
        origin: searchParameters.origin,
        equipment_type: searchParameters.equipment_type,
        radius: searchParameters.radius,
        max_trips: searchParameters.max_trips,
        cost_per_mile: searchParameters.cost_per_mile,
        min_mileage: searchParameters.min_mileage
      },
      auth,
      userData
    });
  }

  /**
   * Convenience method for Heat Map
   */
  async logHeatMap(searchParameters, auth, userData) {
    return this.logActivity({
      functionPage: 'Heat Map',
      searchParameters: {
        equipment_filter: searchParameters.equipment_filter,
        date_range: searchParameters.date_range,
        view_type: searchParameters.view_type
      },
      auth,
      userData
    });
  }

  /**
   * Convenience method for Freight AI
   */
  async logFreightAI(searchParameters, auth, userData) {
    return this.logActivity({
      functionPage: 'Freight AI',
      searchParameters: {
        user_message: searchParameters.user_message
      },
      auth,
      userData
    });
  }
}

// Create singleton instance
const activityLogger = new ActivityLogger();

export default activityLogger;